import {
  Bold,
  Heading2,
  Highlighter,
  ImagePlus,
  Italic,
  Loader2,
} from "lucide-react";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { Button } from "~/components/ui/button";
import { uploadNoteImage } from "~/features/notes/utils/noteImageUpload";
import { cn } from "~/lib/utils";
import logger from "~/utils/logger";
import { useToolbar } from "./toolbar-provider";

const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB
const ALLOWED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/gif"];

export interface MinimalTiptapToolbarProps {
  enableImages?: boolean;
  organizationId?: string;
  projectId?: string;
  imageInputRef?: React.RefObject<HTMLInputElement | null>;
  onImageUploadPendingChange?: (isPending: boolean) => void;
  sticky?: boolean;
}

export const MinimalTiptapToolbar: FC<MinimalTiptapToolbarProps> = ({
  enableImages = false,
  organizationId,
  projectId,
  imageInputRef,
  onImageUploadPendingChange,
  sticky = false,
}) => {
  const { editor } = useToolbar();
  const intl = useIntl();
  const internalFileInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = imageInputRef ?? internalFileInputRef;
  const pendingImageUploadCountRef = useRef(0);
  const isMountedRef = useRef(true);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const setImageUploadPending = useCallback(
    (isPending: boolean) => {
      setIsUploading(isPending);
      onImageUploadPendingChange?.(isPending);
    },
    [onImageUploadPendingChange]
  );

  const startImageUpload = useCallback(() => {
    pendingImageUploadCountRef.current += 1;
    if (pendingImageUploadCountRef.current === 1) {
      setImageUploadPending(true);
    }
  }, [setImageUploadPending]);

  const finishImageUpload = useCallback(() => {
    if (!isMountedRef.current) {
      return;
    }

    pendingImageUploadCountRef.current = Math.max(
      0,
      pendingImageUploadCountRef.current - 1
    );
    if (pendingImageUploadCountRef.current === 0) {
      setImageUploadPending(false);
    }
  }, [setImageUploadPending]);

  const handleImageUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      // Reset input early so the same file can be re-selected after validation errors
      event.target.value = "";
      if (!file) return;

      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        logger.warn("Unsupported image type:", file.type);
        return;
      }

      if (file.size > MAX_IMAGE_SIZE_BYTES) {
        logger.warn("Image too large:", file.size);
        return;
      }

      // Read dimensions from file for the img tag
      const getImageDimensions = (
        file: File
      ): Promise<{ width: number; height: number } | null> =>
        new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => {
            const img = new window.Image();
            img.onload = () =>
              resolve({ width: img.naturalWidth, height: img.naturalHeight });
            img.onerror = () => resolve(null);
            img.src = reader.result as string;
          };
          reader.onerror = () => resolve(null);
          reader.readAsDataURL(file);
        });

      // Upload to S3 via presigned URL if org/project context is available
      if (organizationId && projectId) {
        startImageUpload();
        try {
          const [cdnUrl, dimensions] = await Promise.all([
            uploadNoteImage(organizationId, projectId, file),
            getImageDimensions(file),
          ]);
          editor
            ?.chain()
            .focus()
            .setImage({
              src: cdnUrl,
              alt: file.name,
              ...(dimensions && {
                width: dimensions.width,
                height: dimensions.height,
              }),
            })
            .run();
        } catch (error) {
          logger.error("Failed to upload image to S3:", error);
        } finally {
          finishImageUpload();
        }
      } else {
        // Fallback to base64 if no org context
        startImageUpload();
        const reader = new FileReader();
        reader.onload = () => {
          const dataUrl = reader.result as string;
          const img = new window.Image();
          img.onload = () => {
            editor
              ?.chain()
              .focus()
              .setImage({
                src: dataUrl,
                alt: file.name,
                width: img.naturalWidth,
                height: img.naturalHeight,
              })
              .run();
            finishImageUpload();
          };
          img.onerror = () => {
            editor
              ?.chain()
              .focus()
              .setImage({ src: dataUrl, alt: file.name })
              .run();
            finishImageUpload();
          };
          img.src = dataUrl;
        };
        reader.onerror = () => {
          logger.error("Failed to read image file:", reader.error);
          finishImageUpload();
        };
        reader.readAsDataURL(file);
      }
    },
    [editor, finishImageUpload, organizationId, projectId, startImageUpload]
  );

  if (!editor) {
    return null;
  }

  const ToolbarButton: FC<{
    onClick: () => void;
    isActive?: boolean;
    disabled?: boolean;
    children: React.ReactNode;
    title: string;
  }> = ({ onClick, isActive, disabled, children, title }) => (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={cn(
        "h-8 w-8 p-0",
        isActive && "bg-accent text-accent-foreground"
      )}
    >
      {children}
    </Button>
  );

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-1 border-b border-border p-2",
        sticky ? "sticky top-0 z-10 bg-muted" : "bg-muted/50"
      )}
    >
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        isActive={editor.isActive("bold")}
        disabled={!editor.can().chain().focus().toggleBold().run()}
        title="Bold"
      >
        <Bold className="h-4 w-4" />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        isActive={editor.isActive("italic")}
        disabled={!editor.can().chain().focus().toggleItalic().run()}
        title="Italic"
      >
        <Italic className="h-4 w-4" />
      </ToolbarButton>

      <div className="w-px h-6 bg-border mx-1" />

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        isActive={editor.isActive("heading", { level: 2 })}
        title="Heading"
      >
        <Heading2 className="h-4 w-4" />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleHighlight().run()}
        isActive={editor.isActive("highlight")}
        disabled={!editor.can().chain().focus().toggleHighlight().run()}
        title={intl.formatMessage({
          id: "report.edit.highlight",
          defaultMessage: "Highlight",
        })}
      >
        <Highlighter className="h-4 w-4" />
      </ToolbarButton>

      {enableImages && (
        <>
          <div className="w-px h-6 bg-border mx-1" />
          <ToolbarButton
            onClick={() => fileInputRef.current?.click()}
            title="Add Image"
            disabled={isUploading}
          >
            {isUploading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ImagePlus className="h-4 w-4" />
            )}
          </ToolbarButton>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/gif"
            onChange={handleImageUpload}
            className="hidden"
          />
        </>
      )}
    </div>
  );
};
